// pages/admin/components/ComprehensiveDashboard.js - Complete Dashboard Components
import React from 'react';
import { FaUsers, FaCrown, FaDollarSign, FaFileAlt, FaChartLine, FaExchangeAlt, FaEnvelope } from 'react-icons/fa';

// Helper function to safely format numbers
const safeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? defaultValue : num;
};

// Helper function to safely format strings
const safeString = (value, defaultValue = "0.00") => {
  return value !== null && value !== undefined ? String(value) : defaultValue;
};

// Helper function to safely format numbers with locale
const safeLocaleString = (value) => {
  const num = safeNumber(value);
  return num.toLocaleString();
};

// OverviewStats Component - Key Performance Indicators
const OverviewStats = ({ stats }) => {
  if (!stats) {
    return (
      <div className="stats-card overview-stats loading">
        <h2>📊 Key Performance Indicators</h2>
        <p>Loading overview...</p>
      </div>
    );
  }

  return (
    <div className="stats-card overview-stats">
      <h2>📊 Key Performance Indicators</h2>
      <div className="stats-grid">
        <div className="stat-item highlight-item">
          <div className="stat-icon"><FaUsers /></div>
          <div className="stat-content">
            <h3>Total Users</h3>
            <p className="stat-value">{safeLocaleString(stats.totalUsers)}</p>
          </div>
        </div>

        <div className="stat-item highlight-item">
          <div className="stat-icon"><FaCrown /></div>
          <div className="stat-content">
            <h3>Active Subscribers</h3>
            <p className="stat-value">{safeLocaleString(stats.activeSubscribers)}</p>
          </div>
        </div>

        <div className="stat-item highlight-item">
          <div className="stat-icon"><FaExchangeAlt /></div>
          <div className="stat-content">
            <h3>Conversion Rate</h3>
            <p className="stat-value">{safeString(stats.conversionRate)}%</p>
          </div>
        </div>

        <div className="stat-item highlight-item">
          <div className="stat-icon"><FaDollarSign /></div>
          <div className="stat-content">
            <h3>Monthly Revenue (USD)</h3>
            <p className="stat-value">${safeString(stats.revenueMonth_USD)}</p>
          </div>
        </div>

        <div className="stat-item highlight-item">
          <div className="stat-icon"><FaDollarSign /></div>
          <div className="stat-content">
            <h3>Monthly Revenue (INR)</h3>
            <p className="stat-value">₹{safeString(stats.revenueMonth_INR)}</p>
          </div>
        </div>

        <div className="stat-item highlight-item">
          <div className="stat-icon"><FaFileAlt /></div>
          <div className="stat-content">
            <h3>Total Documents</h3>
            <p className="stat-value">{safeLocaleString(safeNumber(stats.totalResumes) + safeNumber(stats.totalCoverLetters))}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// UserAnalytics Component - Detailed User Metrics
const UserAnalytics = ({ stats }) => {
  if (!stats) {
    return (
      <div className="stats-card user-analytics loading">
        <h2>👥 User Analytics</h2>
        <p>Loading user analytics...</p>
      </div>
    );
  }

  return (
    <div className="stats-card user-analytics">
      <h2>👥 User Analytics</h2>
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-icon"><FaUsers /></div>
          <div className="stat-content">
            <h3>New Users Today</h3>
            <p className="stat-value">{safeNumber(stats.newUsersToday)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaUsers /></div>
          <div className="stat-content">
            <h3>New Users (Week)</h3>
            <p className="stat-value">{safeNumber(stats.newUsersWeek)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaUsers /></div>
          <div className="stat-content">
            <h3>New Users (Month)</h3>
            <p className="stat-value">{safeNumber(stats.newUsersMonth)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaUsers /></div>
          <div className="stat-content">
            <h3>Free Users</h3>
            <p className="stat-value">{safeLocaleString(stats.freeUsers)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaChartLine /></div>
          <div className="stat-content">
            <h3>Active Users Today</h3>
            <p className="stat-value">{safeNumber(stats.activeUsersToday)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaChartLine /></div>
          <div className="stat-content">
            <h3>Active Users (Week)</h3>
            <p className="stat-value">{safeNumber(stats.activeUsersWeek)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// SubscriptionAnalytics Component - Subscription Metrics
const SubscriptionAnalytics = ({ stats }) => {
  if (!stats) {
    return (
      <div className="stats-card subscription-analytics loading">
        <h2>💎 Subscription Analytics</h2>
        <p>Loading subscription analytics...</p>
      </div>
    );
  }

  return (
    <div className="stats-card subscription-analytics">
      <h2>💎 Subscription Analytics</h2>
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-icon"><FaCrown /></div>
          <div className="stat-content">
            <h3>New Subscribers Today</h3>
            <p className="stat-value">{safeNumber(stats.newSubscribersToday)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaCrown /></div>
          <div className="stat-content">
            <h3>New Subscribers (Week)</h3>
            <p className="stat-value">{safeNumber(stats.newSubscribersWeek)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaCrown /></div>
          <div className="stat-content">
            <h3>New Subscribers (Month)</h3>
            <p className="stat-value">{safeNumber(stats.newSubscribersMonth)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaCrown /></div>
          <div className="stat-content">
            <h3>7-Day Plan Users</h3>
            <p className="stat-value">{safeNumber(stats.sevenDaySubscribers)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaCrown /></div>
          <div className="stat-content">
            <h3>1-Month Plan Users</h3>
            <p className="stat-value">{safeNumber(stats.oneMonthSubscribers)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaCrown /></div>
          <div className="stat-content">
            <h3>3-Month Plan Users</h3>
            <p className="stat-value">{safeNumber(stats.threeMonthSubscribers)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// RevenueAnalytics Component - Financial Metrics with Currency Separation
const RevenueAnalytics = ({ stats }) => {
  if (!stats) {
    return (
      <div className="stats-card revenue-analytics loading">
        <h2>💰 Revenue Analytics</h2>
        <p>Loading revenue analytics...</p>
      </div>
    );
  }

  return (
    <div className="stats-card revenue-analytics">
      <h2>💰 Revenue Analytics</h2>
      
      {/* USD Revenue Section */}
      <div className="currency-section">
        <h3 className="currency-header">🇺🇸 USD Revenue</h3>
        <div className="stats-grid">
          <div className="stat-item usd-item">
            <div className="stat-icon"><FaDollarSign /></div>
            <div className="stat-content">
              <h3>Today</h3>
              <p className="stat-value">${safeString(stats.revenueToday_USD)}</p>
            </div>
          </div>

          <div className="stat-item usd-item">
            <div className="stat-icon"><FaDollarSign /></div>
            <div className="stat-content">
              <h3>This Week</h3>
              <p className="stat-value">${safeString(stats.revenueWeek_USD)}</p>
            </div>
          </div>

          <div className="stat-item usd-item">
            <div className="stat-icon"><FaDollarSign /></div>
            <div className="stat-content">
              <h3>This Month</h3>
              <p className="stat-value">${safeString(stats.revenueMonth_USD)}</p>
            </div>
          </div>

          <div className="stat-item usd-item">
            <div className="stat-icon"><FaDollarSign /></div>
            <div className="stat-content">
              <h3>This Year</h3>
              <p className="stat-value">${safeString(stats.revenueYear_USD)}</p>
            </div>
          </div>

          <div className="stat-item usd-item">
            <div className="stat-icon"><FaChartLine /></div>
            <div className="stat-content">
              <h3>Avg Order Value</h3>
              <p className="stat-value">${safeString(stats.averageOrderValue_USD)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* INR Revenue Section */}
      <div className="currency-section">
        <h3 className="currency-header">🇮🇳 INR Revenue</h3>
        <div className="stats-grid">
          <div className="stat-item inr-item">
            <div className="stat-icon"><FaDollarSign /></div>
            <div className="stat-content">
              <h3>Today</h3>
              <p className="stat-value">₹{safeString(stats.revenueToday_INR)}</p>
            </div>
          </div>

          <div className="stat-item inr-item">
            <div className="stat-icon"><FaDollarSign /></div>
            <div className="stat-content">
              <h3>This Week</h3>
              <p className="stat-value">₹{safeString(stats.revenueWeek_INR)}</p>
            </div>
          </div>

          <div className="stat-item inr-item">
            <div className="stat-icon"><FaDollarSign /></div>
            <div className="stat-content">
              <h3>This Month</h3>
              <p className="stat-value">₹{safeString(stats.revenueMonth_INR)}</p>
            </div>
          </div>

          <div className="stat-item inr-item">
            <div className="stat-icon"><FaDollarSign /></div>
            <div className="stat-content">
              <h3>This Year</h3>
              <p className="stat-value">₹{safeString(stats.revenueYear_INR)}</p>
            </div>
          </div>

          <div className="stat-item inr-item">
            <div className="stat-icon"><FaChartLine /></div>
            <div className="stat-content">
              <h3>Avg Order Value</h3>
              <p className="stat-value">₹{safeString(stats.averageOrderValue_INR)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// DocumentAnalytics Component - Document Creation Metrics
const DocumentAnalytics = ({ stats }) => {
  if (!stats) {
    return (
      <div className="stats-card document-analytics loading">
        <h2>📄 Document Analytics</h2>
        <p>Loading document analytics...</p>
      </div>
    );
  }

  return (
    <div className="stats-card document-analytics">
      <h2>📄 Document Analytics</h2>
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-icon"><FaFileAlt /></div>
          <div className="stat-content">
            <h3>Total Resumes</h3>
            <p className="stat-value">{safeLocaleString(stats.totalResumes)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaEnvelope /></div>
          <div className="stat-content">
            <h3>Total Cover Letters</h3>
            <p className="stat-value">{safeLocaleString(stats.totalCoverLetters)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaFileAlt /></div>
          <div className="stat-content">
            <h3>Resumes Today</h3>
            <p className="stat-value">{safeNumber(stats.resumesCreatedToday)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaEnvelope /></div>
          <div className="stat-content">
            <h3>Cover Letters Today</h3>
            <p className="stat-value">{safeNumber(stats.coverLettersCreatedToday)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaFileAlt /></div>
          <div className="stat-content">
            <h3>Resumes (Week)</h3>
            <p className="stat-value">{safeNumber(stats.resumesCreatedWeek)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaEnvelope /></div>
          <div className="stat-content">
            <h3>Cover Letters (Week)</h3>
            <p className="stat-value">{safeNumber(stats.coverLettersCreatedWeek)}</p>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon"><FaChartLine /></div>
          <div className="stat-content">
            <h3>Avg Docs per User</h3>
            <p className="stat-value">{safeString(stats.averageDocumentsPerUser)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// PaymentAnalytics Component - Payment Success/Failure Metrics
const PaymentAnalytics = ({ stats }) => {
  if (!stats) {
    return (
      <div className="stats-card payment-analytics loading">
        <h2>💳 Payment Analytics</h2>
        <p>Loading payment analytics...</p>
      </div>
    );
  }

  return (
    <div className="stats-card payment-analytics">
      <h2>💳 Payment Analytics</h2>
      <div className="stats-grid">
        <div className="stat-item success-item">
          <div className="stat-icon"><FaChartLine /></div>
          <div className="stat-content">
            <h3>Successful Payments</h3>
            <p className="stat-value">{safeLocaleString(stats.successfulPayments)}</p>
          </div>
        </div>

        <div className="stat-item error-item">
          <div className="stat-icon"><FaExchangeAlt /></div>
          <div className="stat-content">
            <h3>Failed Payments</h3>
            <p className="stat-value">{safeLocaleString(stats.failedPayments)}</p>
          </div>
        </div>

        <div className="stat-item warning-item">
          <div className="stat-icon"><FaChartLine /></div>
          <div className="stat-content">
            <h3>Payment Attempts</h3>
            <p className="stat-value">{safeLocaleString(stats.paymentAttempts)}</p>
          </div>
        </div>

        <div className="stat-item highlight-item">
          <div className="stat-icon"><FaChartLine /></div>
          <div className="stat-content">
            <h3>Success Rate</h3>
            <p className="stat-value">{safeString(stats.paymentSuccessRate)}%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export all components
export { 
  OverviewStats, 
  UserAnalytics, 
  SubscriptionAnalytics, 
  RevenueAnalytics, 
  DocumentAnalytics, 
  PaymentAnalytics 
};